import { getSupabaseClient, isSupabaseConfigured, signInAnonymously, subscribeToChanges, unsubscribeFromChanges } from './supabase';
import type { HealthRecord, BloodPressureRecord, BloodSugarRecord, SyncStatus } from '@/types';
import type { RealtimeChannel } from '@supabase/supabase-js';

// 同步队列项
interface SyncQueueItem {
  id: string;
  type: 'health_record' | 'blood_pressure' | 'blood_sugar';
  action: 'create' | 'update' | 'delete';
  data: any;
  retryCount: number;
  timestamp: number;
}

// 同步服务状态
interface SyncState {
  isInitialized: boolean;
  isOnline: boolean;
  lastSyncAt: number | null;
  syncQueue: SyncQueueItem[];
  currentUserId: string | null;
  realtimeChannel: RealtimeChannel | null;
  status: SyncStatus;
  isProcessing: boolean;
}

// 生成简单UUID（兼容模式）
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// 生成设备ID
const getDeviceId = (): string => {
  let deviceId = localStorage.getItem('health_monitor_device_id');
  if (!deviceId) {
    deviceId = `device_${generateUUID()}`;
    localStorage.setItem('health_monitor_device_id', deviceId);
  }
  return deviceId;
};

// 生成唯一队列ID
const generateQueueId = (): string => {
  return `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// 同步服务状态
const state: SyncState = {
  isInitialized: false,
  isOnline: navigator.onLine,
  lastSyncAt: null,
  syncQueue: [],
  currentUserId: null,
  realtimeChannel: null,
  status: 'idle',
  isProcessing: false
};

// 状态监听器
const statusListeners: Set<(status: SyncStatus, details?: string) => void> = new Set();

// 通知状态变更
const notifyStatusChange = (status: SyncStatus, details?: string) => {
  state.status = status;
  statusListeners.forEach(listener => listener(status, details));
};

// 添加状态监听器
export const addSyncStatusListener = (listener: (status: SyncStatus, details?: string) => void) => {
  statusListeners.add(listener);
  // 立即通知当前状态
  listener(state.status);
  return () => statusListeners.delete(listener);
};

// 获取当前同步状态
export const getSyncStatus = (): { status: SyncStatus; isOnline: boolean; lastSyncAt: number | null } => {
  return {
    status: state.status,
    isOnline: state.isOnline,
    lastSyncAt: state.lastSyncAt
  };
};

// 检查网络状态
const checkNetworkStatus = (): boolean => {
  return navigator.onLine && isSupabaseConfigured();
};

// 保存同步队列到本地存储
const saveSyncQueue = () => {
  localStorage.setItem('health_sync_queue', JSON.stringify(state.syncQueue));
};

// 加载同步队列
const loadSyncQueue = () => {
  try {
    const queue = localStorage.getItem('health_sync_queue');
    if (queue) {
      state.syncQueue = JSON.parse(queue);
    }
  } catch (e) {
    console.error('Failed to load sync queue:', e);
    state.syncQueue = [];
  }
};

// 添加到同步队列
const addToSyncQueue = (item: Omit<SyncQueueItem, 'id' | 'retryCount' | 'timestamp'>) => {
  console.log('[Sync] Adding to queue:', item.type, 'Online:', state.isOnline, 'Processing:', state.isProcessing);
  
  const queueItem: SyncQueueItem = {
    ...item,
    id: generateQueueId(),
    retryCount: 0,
    timestamp: Date.now()
  };
  state.syncQueue.push(queueItem);
  saveSyncQueue();
  
  console.log('[Sync] Queue length:', state.syncQueue.length);
  
  // 如果在线，尝试立即同步
  if (state.isOnline && !state.isProcessing) {
    console.log('[Sync] Triggering immediate sync');
    processSyncQueue();
  } else {
    console.log('[Sync] Not triggering sync. Online:', state.isOnline, 'Processing:', state.isProcessing);
  }
};

// 从队列中移除
const removeFromQueue = (id: string) => {
  state.syncQueue = state.syncQueue.filter(item => item.id !== id);
  saveSyncQueue();
};

// 增加重试计数
const incrementRetry = (id: string) => {
  const item = state.syncQueue.find(i => i.id === id);
  if (item) {
    item.retryCount++;
    saveSyncQueue();
  }
};

// 同步单条记录到云端
const syncItemToCloud = async (item: SyncQueueItem): Promise<boolean> => {
  console.log('[Sync] Attempting to sync item:', item.type, 'User:', state.currentUserId);
  
  if (!state.currentUserId) {
    console.log('[Sync] No user ID, skipping');
    return false;
  }
  
  // 本地用户不同步到云端
  if (state.currentUserId.startsWith('local_')) {
    console.log('[Sync] Local user, skipping cloud sync');
    return false;
  }
  
  const supabase = getSupabaseClient();
  const deviceId = getDeviceId();
  
  console.log('[Sync] Supabase client created, device:', deviceId);
  
  try {
    switch (item.type) {
      case 'health_record': {
        const recordData = {
          id: item.data.id,
          user_id: state.currentUserId,
          date: item.data.date,
          type: item.data.type,
          notes: item.data.notes,
          device_id: deviceId,
          is_deleted: item.action === 'delete',
          updated_at: new Date().toISOString()
        };
        
        console.log('[Sync] Upserting health record:', recordData);
        const { data, error } = await supabase
          .from('health_records_sync')
          .upsert(recordData, { onConflict: 'id' })
          .select();
          
        if (error) {
          console.error('[Sync] Health record sync error:', error);
          console.error('[Sync] Error details:', JSON.stringify(error));
          return false;
        }
        console.log('[Sync] Health record synced successfully:', data);
        break;
      }
      
      case 'blood_pressure': {
        const bpData = {
          id: item.data.id,
          record_id: item.data.recordId,
          user_id: state.currentUserId,
          date: item.data.date,
          systolic: item.data.systolic,
          diastolic: item.data.diastolic,
          heart_rate: item.data.heartRate,
          measure_time: item.data.measureTime,
          updated_at: new Date().toISOString()
        };
        
        console.log('[Sync] Upserting BP record:', bpData);
        if (item.action === 'delete') {
          const { error } = await supabase
            .from('blood_pressure_sync')
            .delete()
            .eq('id', item.data.id);
          if (error) {
            console.error('[Sync] BP delete error:', error);
            console.error('[Sync] Error details:', JSON.stringify(error));
            return false;
          }
        } else {
          const { data, error } = await supabase
            .from('blood_pressure_sync')
            .upsert(bpData, { onConflict: 'id' })
            .select();
          if (error) {
            console.error('[Sync] BP sync error:', error);
            console.error('[Sync] Error details:', JSON.stringify(error));
            return false;
          }
          console.log('[Sync] BP record synced successfully:', data);
        }
        break;
      }
      
      case 'blood_sugar': {
        const bsData = {
          id: item.data.id,
          record_id: item.data.recordId,
          user_id: state.currentUserId,
          date: item.data.date,
          value: item.data.value,
          type: item.data.type,
          updated_at: new Date().toISOString()
        };
        
        if (item.action === 'delete') {
          const { error } = await supabase
            .from('blood_sugar_sync')
            .delete()
            .eq('id', item.data.id);
          if (error) {
            console.error('BS delete error:', error);
            return false;
          }
        } else {
          const { error } = await supabase
            .from('blood_sugar_sync')
            .upsert(bsData, { onConflict: 'id' });
          if (error) {
            console.error('BS sync error:', error);
            return false;
          }
        }
        break;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Sync item failed:', error);
    return false;
  }
};

// 处理同步队列
const processSyncQueue = async () => {
  if (state.isProcessing || !state.isOnline || state.syncQueue.length === 0) {
    return;
  }
  
  state.isProcessing = true;
  notifyStatusChange('syncing', `正在同步 ${state.syncQueue.length} 条记录...`);
  
  // 按时间戳排序，先同步旧的
  const sortedQueue = [...state.syncQueue].sort((a, b) => a.timestamp - b.timestamp);
  
  for (const item of sortedQueue) {
    // 跳过重试次数过多的
    if (item.retryCount >= 3) {
      console.warn('Sync item exceeded retry limit:', item);
      removeFromQueue(item.id);
      continue;
    }
    
    const success = await syncItemToCloud(item);
    
    if (success) {
      console.log('[Sync] Item synced successfully, removing from queue:', item.id);
      removeFromQueue(item.id);
    } else {
      console.log('[Sync] Item sync failed, incrementing retry:', item.id);
      incrementRetry(item.id);
    }
  }
  
  state.isProcessing = false;
  console.log('[Sync] Queue processing complete. Remaining items:', state.syncQueue.length);
  
  // 更新最后同步时间
  if (state.syncQueue.length === 0) {
    state.lastSyncAt = Date.now();
    localStorage.setItem('health_last_sync_at', state.lastSyncAt.toString());
    notifyStatusChange('synced', '同步完成');
  } else {
    notifyStatusChange('error', `${state.syncQueue.length} 条记录同步失败`);
  }
  
  // 如果还有队列项，延迟后重试
  if (state.syncQueue.length > 0) {
    setTimeout(() => processSyncQueue(), 30000); // 30秒后重试
  }
};

// 从云端拉取数据
const pullFromCloud = async (since?: number): Promise<boolean> => {
  if (!state.currentUserId) {
    console.log('[Sync] Pull failed: no current user');
    return false;
  }
  
  notifyStatusChange('syncing', '正在从云端获取数据...');
  
  try {
    const supabase = getSupabaseClient();
    // const deviceId = getDeviceId(); // 暂不使用
    
    // 构建查询条件
    const sinceDate = since ? new Date(since).toISOString() : new Date(0).toISOString();
    console.log('[Sync] Pulling from cloud. User:', state.currentUserId, 'Since:', sinceDate);
    
    // 拉取健康记录
    console.log('[Sync] Querying health_records_sync...');
    const { data: records, error: recordsError } = await supabase
      .from('health_records_sync')
      .select('*')
      .eq('user_id', state.currentUserId)
      .gt('updated_at', sinceDate)
      .order('updated_at', { ascending: true });
    
    console.log('[Sync] Records query result:', { count: records?.length, error: recordsError?.message });
    
    if (recordsError) {
      console.error('[Sync] Records query error:', recordsError);
      throw recordsError;
    }
    console.log('[Sync] Health records found:', records?.length || 0);
    
    // 拉取血压记录
    console.log('[Sync] Querying blood_pressure_sync...');
    const { data: bpRecords, error: bpError } = await supabase
      .from('blood_pressure_sync')
      .select('*')
      .eq('user_id', state.currentUserId)
      .gt('updated_at', sinceDate)
      .order('updated_at', { ascending: true });
    
    if (bpError) {
      console.error('[Sync] BP query error:', bpError);
      throw bpError;
    }
    console.log('[Sync] BP records found:', bpRecords?.length || 0);
    
    // 拉取血糖记录
    console.log('[Sync] Querying blood_sugar_sync...');
    const { data: bsRecords, error: bsError } = await supabase
      .from('blood_sugar_sync')
      .select('*')
      .eq('user_id', state.currentUserId)
      .gt('updated_at', sinceDate)
      .order('updated_at', { ascending: true });
    
    if (bsError) {
      console.error('[Sync] BS query error:', bsError);
      throw bsError;
    }
    console.log('[Sync] BS records found:', bsRecords?.length || 0);
    
    // 触发事件，让 stores 处理数据合并
    const event = new CustomEvent('cloudDataPulled', {
      detail: {
        records: records || [],
        bpRecords: bpRecords || [],
        bsRecords: bsRecords || []
      }
    });
    window.dispatchEvent(event);
    
    return true;
  } catch (error) {
    console.error('Pull from cloud failed:', error);
    return false;
  }
};

// 处理实时变更
const handleRealtimeChange = (table: string, payload: any) => {
  // 忽略自己设备产生的变更
  const deviceId = getDeviceId();
  if (payload.new?.device_id === deviceId || payload.old?.device_id === deviceId) {
    return;
  }
  
  // 触发事件，让 stores 处理
  const event = new CustomEvent('realtimeDataChange', {
    detail: { table, payload }
  });
  window.dispatchEvent(event);
  
  // 通知用户有新数据
  notifyStatusChange('synced', '收到云端更新');
};

// 初始化同步服务
export const initSyncService = async (): Promise<boolean> => {
  if (state.isInitialized) return true;
  
  // 检查是否配置了 Supabase
  if (!isSupabaseConfigured()) {
    notifyStatusChange('offline', '未配置云端同步');
    state.isInitialized = true;
    return false;
  }
  
  notifyStatusChange('syncing', '正在初始化同步服务...');
  
  try {
    // 登录获取用户ID
    const { userId } = await signInAnonymously();
    
    // 匿名登录失败时，signInAnonymously 会返回本地用户ID
    // 所以只要有 userId，就认为初始化成功（本地模式）
    if (!userId) {
      notifyStatusChange('offline', '登录失败，使用本地模式');
      state.isOnline = false;
      state.isInitialized = true;
      return false;
    }
    
    state.currentUserId = userId;
    // 本地用户不使用云端同步
    state.isOnline = !userId.startsWith('local_') && checkNetworkStatus();
    
    // 加载同步队列
    loadSyncQueue();
    
    // 加载最后同步时间
    const lastSync = localStorage.getItem('health_last_sync_at');
    const hasSyncedBefore = localStorage.getItem('health_has_synced_before');
    
    if (lastSync && hasSyncedBefore) {
      state.lastSyncAt = parseInt(lastSync, 10);
      console.log('[Sync] Last sync at:', new Date(state.lastSyncAt).toISOString());
    } else {
      // 首次同步，全量拉取
      state.lastSyncAt = null;
      console.log('[Sync] First sync on this device, will pull all data');
    }
    
    // 监听网络状态
    window.addEventListener('online', () => {
      state.isOnline = checkNetworkStatus();
      if (state.isOnline) {
        notifyStatusChange('syncing', '网络已恢复，开始同步...');
        processSyncQueue();
      } else {
        notifyStatusChange('offline', '已切换到离线模式');
      }
    });
    
    window.addEventListener('offline', () => {
      state.isOnline = false;
      notifyStatusChange('offline', '网络已断开');
    });
    
    // 订阅实时变更（仅云端用户）
    if (!userId.startsWith('local_')) {
      state.realtimeChannel = subscribeToChanges(
        userId,
        (payload) => handleRealtimeChange('health_records_sync', payload),
        (payload) => handleRealtimeChange('blood_pressure_sync', payload),
        (payload) => handleRealtimeChange('blood_sugar_sync', payload)
      );
    }
    
    // 执行初始全量同步（仅云端用户）
    if (!userId.startsWith('local_')) {
      await performFullSync();
    }
    
    state.isInitialized = true;
    return true;
  } catch (error) {
    console.error('Failed to initialize sync service:', error);
    notifyStatusChange('offline', '初始化失败');
    state.isInitialized = true;
    return false;
  }
};

// 执行全量同步
export const performFullSync = async (): Promise<boolean> => {
  if (!state.isOnline || !state.currentUserId) {
    notifyStatusChange('offline', '当前处于离线模式');
    return false;
  }
  
  notifyStatusChange('syncing', '正在全量同步...');
  
  try {
    // 先推送本地变更
    await processSyncQueue();
    
    // 再拉取云端数据
    const success = await pullFromCloud(state.lastSyncAt || undefined);
    
    if (success) {
      state.lastSyncAt = Date.now();
      localStorage.setItem('health_last_sync_at', state.lastSyncAt.toString());
      notifyStatusChange('synced', '同步完成');
    }
    
    return success;
  } catch (error) {
    console.error('Full sync failed:', error);
    notifyStatusChange('error', '同步失败');
    return false;
  }
};

// 同步单条健康记录
export const syncHealthRecord = (record: HealthRecord, action: 'create' | 'update' | 'delete' = 'create') => {
  addToSyncQueue({
    type: 'health_record',
    action,
    data: record
  });
};

// 同步血压记录
export const syncBloodPressureRecord = (record: BloodPressureRecord, action: 'create' | 'update' | 'delete' = 'create') => {
  addToSyncQueue({
    type: 'blood_pressure',
    action,
    data: record
  });
};

// 同步血糖记录
export const syncBloodSugarRecord = (record: BloodSugarRecord, action: 'create' | 'update' | 'delete' = 'create') => {
  addToSyncQueue({
    type: 'blood_sugar',
    action,
    data: record
  });
};

// 手动触发同步
export const triggerManualSync = async (): Promise<boolean> => {
  return performFullSync();
};

// 清理同步服务
export const cleanupSyncService = () => {
  if (state.realtimeChannel) {
    unsubscribeFromChanges(state.realtimeChannel);
    state.realtimeChannel = null;
  }
  state.isInitialized = false;
};

// 获取同步统计信息
export const getSyncStats = () => {
  return {
    queueLength: state.syncQueue.length,
    lastSyncAt: state.lastSyncAt,
    isOnline: state.isOnline,
    isProcessing: state.isProcessing,
    currentUserId: state.currentUserId
  };
};

// 强制推送所有本地数据到云端（用于首次同步或数据恢复）
export const forcePushAllData = async (
  healthRecords: HealthRecord[],
  bpRecords: BloodPressureRecord[],
  bsRecords: BloodSugarRecord[]
): Promise<{ success: number; failed: number }> => {
  if (!state.currentUserId) {
    console.error('[Sync] No current user, cannot push data');
    return { success: 0, failed: 0 };
  }
  
  // 本地用户不能推送到云端
  if (state.currentUserId.startsWith('local_')) {
    console.error('[Sync] Local user cannot push to cloud');
    notifyStatusChange('offline', '本地用户无法推送到云端，请先登录');
    return { success: 0, failed: 0 };
  }
  
  console.log('[Sync] Force pushing all data to cloud:', {
    healthRecords: healthRecords.length,
    bpRecords: bpRecords.length,
    bsRecords: bsRecords.length
  });
  
  notifyStatusChange('syncing', `正在推送 ${healthRecords.length + bpRecords.length + bsRecords.length} 条记录到云端...`);
  
  let success = 0;
  let failed = 0;
  
  try {
    const supabase = getSupabaseClient();
    
    // 推送健康记录
    for (const record of healthRecords) {
      try {
        const recordData = {
          id: record.id,
          user_id: state.currentUserId,
          date: record.date,
          type: record.type,
          notes: record.notes,
          device_id: getDeviceId(),
          is_deleted: false,
          updated_at: new Date().toISOString()
        };
        
        const { error } = await supabase
          .from('health_records_sync')
          .upsert(recordData, { onConflict: 'id' });
        
        if (error) {
          console.error('[Sync] Failed to push health record:', record.id, error);
          failed++;
        } else {
          success++;
        }
      } catch (e) {
        console.error('[Sync] Exception pushing health record:', record.id, e);
        failed++;
      }
    }
    
    // 推送血压记录
    for (const record of bpRecords) {
      try {
        const bpData = {
          id: record.id,
          record_id: record.recordId,
          user_id: state.currentUserId,
          date: record.date,
          systolic: record.systolic,
          diastolic: record.diastolic,
          heart_rate: record.heartRate,
          measure_time: record.measureTime,
          updated_at: new Date().toISOString()
        };
        
        const { error } = await supabase
          .from('blood_pressure_sync')
          .upsert(bpData, { onConflict: 'id' });
        
        if (error) {
          console.error('[Sync] Failed to push BP record:', record.id, error);
          failed++;
        } else {
          success++;
        }
      } catch (e) {
        console.error('[Sync] Exception pushing BP record:', record.id, e);
        failed++;
      }
    }
    
    // 推送血糖记录
    for (const record of bsRecords) {
      try {
        const bsData = {
          id: record.id,
          record_id: record.recordId,
          user_id: state.currentUserId,
          date: record.date,
          value: record.value,
          type: record.type,
          updated_at: new Date().toISOString()
        };
        
        const { error } = await supabase
          .from('blood_sugar_sync')
          .upsert(bsData, { onConflict: 'id' });
        
        if (error) {
          console.error('[Sync] Failed to push BS record:', record.id, error);
          failed++;
        } else {
          success++;
        }
      } catch (e) {
        console.error('[Sync] Exception pushing BS record:', record.id, e);
        failed++;
      }
    }
    
    console.log('[Sync] Force push complete:', { success, failed });
    
    if (failed === 0) {
      notifyStatusChange('synced', `成功推送 ${success} 条记录到云端`);
    } else {
      notifyStatusChange('error', `推送完成：${success} 成功，${failed} 失败`);
    }
    
    return { success, failed };
  } catch (error) {
    console.error('[Sync] Force push failed:', error);
    notifyStatusChange('error', '推送数据到云端失败');
    return { success, failed };
  }
};
